// ------------------------------
// facet-filters-form.js
// ------------------------------

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // Immediate visual feedback before debounce fires
    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', () => {
      const grid = document.getElementById('ProductGridContainer');
      if (grid) grid.classList.add('loading');
      // Show loading on filter panel if it's open
      const filterPanel = document.getElementById('ProductGridFilters');
      if (filterPanel && filterPanel.classList.contains('show-filters')) {
        filterPanel.classList.add('loading');
      }
    });

    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((el) =>
      el.classList.toggle('disabled', disable)
    );
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const requestId = ++FacetFiltersForm.requestId;
    const sections = FacetFiltersForm.getSections();
    // Si no hay grid que renderizar, salimos ANTES de pintar el overlay de carga:
    // de lo contrario quedaría el spinner puesto sin que nada lo retire nunca.
    if (!sections.length) {
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      return;
    }
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) gridContainer.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  // Deja la UI en estado usable pase lo que pase: sin esto, una excepción a mitad
  // de render congela los filtros con el overlay de carga puesto.
  static clearLoadingState() {
    ['ProductGridContainer', 'ProductGridFilters', 'ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.classList.remove('loading');
    });
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderSectionFromFetch(url, event, requestId) {
    fetch(url)
      .then((response) => {
        // Shopify devuelve 430/429 al limitar peticiones y 5xx en errores transitorios;
        // esos cuerpos NO son la sección. Sin este check se renderizarían como válidos y
        // borrarían todos los filtros y el trigger de paginación.
        if (!response.ok) throw new Error('Section request failed: ' + response.status);
        return response.text();
      })
      .then((html) => {
        // Parseamos UNA sola vez y reutilizamos el documento en todos los render.
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Validación de estructura: un 200 con cuerpo equivocado (redirección a
        // password, página de error cacheada) no debe destruir la UI existente.
        if (!doc.getElementById('ProductGridContainer')) {
          if (requestId === FacetFiltersForm.requestId) FacetFiltersForm.clearLoadingState();
          return;
        }

        // Solo cacheamos respuestas válidas (si cacheáramos antes, un error quedaría
        // guardado y se re-serviría desde caché).
        FacetFiltersForm.filterData.push({ html, url });

        // Una petición anterior puede llegar después de otra más reciente. Renderizarla
        // pintaría resultados de un filtro que el usuario ya cambió.
        if (requestId !== FacetFiltersForm.requestId) return;

        FacetFiltersForm.renderSection(doc, event);
      })
      .catch((error) => {
        console.warn('Facet filtering failed:', error);
        if (requestId !== FacetFiltersForm.requestId) return;
        FacetFiltersForm.clearLoadingState();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const { html } = FacetFiltersForm.filterData.find(filterDataUrl);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderSection(doc, event);
  }

  // Pipeline de render compartido por fetch y caché. Recibe el documento ya parseado.
  static renderSection(doc, event) {
    try {
      FacetFiltersForm.renderFilters(doc, event);
      FacetFiltersForm.renderProductGridContainer(doc);
      FacetFiltersForm.renderProductCount(doc);
      FacetFiltersForm.renderPagination(doc);
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger();
      }

      // Re-inicializamos el slider al terminar el render
      initPriceSlider();

      // Dispatch event so Ajaxinate and other enhancements re-initialize
      document.dispatchEvent(new CustomEvent('facets:updated'));
    } finally {
      FacetFiltersForm.clearLoadingState();
    }
  }

  // La paginación vive FUERA de #ProductGridContainer, así que renderProductGridContainer
  // no la toca. Si no la refrescamos aquí, el enlace "siguiente" (.infinite_next) sigue
  // apuntando a la página del filtro ANTERIOR y el scroll infinito inyecta productos
  // que no cumplen el filtro activo.
  static renderPagination(doc) {
    const source = doc.querySelector('.pagination-wrapper');
    const current = document.querySelector('.pagination-wrapper');

    if (source && current) {
      current.innerHTML = source.innerHTML;
    } else if (!source && current) {
      // El nuevo resultado cabe en una sola página: no hay "siguiente" que seguir.
      current.innerHTML = '';
    } else if (source && !current) {
      // Caso raro (el resultado inicial no paginaba y ahora sí): lo colocamos tras el grid.
      const grid = document.getElementById('ProductGridContainer');
      const anchor = grid ? grid.closest('section') || grid : null;
      if (anchor) anchor.insertAdjacentHTML('afterend', source.outerHTML);
    }
  }

  static renderProductGridContainer(doc) {
    const container = document.getElementById('ProductGridContainer');
    const source = doc.getElementById('ProductGridContainer');
    if (!container || !source) return;

    container.innerHTML = source.innerHTML;

    container.classList.remove('loading');

    // Remove filter panel loading
    const filterPanel = document.getElementById('ProductGridFilters');
    if (filterPanel) filterPanel.classList.remove('loading');

    container
      .querySelectorAll('.scroll-trigger')
      .forEach((el) => el.classList.add('scroll-trigger--cancel'));

    // initInfiniteScroll();
  }

  static renderProductCount(doc) {
    const source = doc.getElementById('ProductCount');
    if (!source) return;
    const count = source.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    document
      .querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      )
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedHTML, event) {
    const fetched = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const current = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Eliminar filtros que ya no vienen
    Array.from(current).forEach((el) => {
      if (!Array.from(fetched).some(({ id }) => el.id === id)) el.remove();
    });

    const matches = (el) => {
      const jsf = event ? event.target.closest('.js-filter') : null;
      return jsf ? el.id === jsf.id : false;
    };
    const toRender = Array.from(fetched).filter((el) => !matches(el));
    const countsToRender = Array.from(fetched).find(matches);

    toRender.forEach((el, i) => {
      const exists = document.getElementById(el.id);
      if (exists) {
        exists.innerHTML = el.innerHTML;
      } else {
        if (i > 0 && el.className === toRender[i - 1].className) {
          document.getElementById(toRender[i - 1].id).after(el);
        } else if (el.parentElement) {
          document
            .querySelector(`#${el.parentElement.id} .js-filter`)
            .before(el);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const parentId = event.target.closest('.js-filter').id;
      if (parentId) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(parentId));

        const newElem = document.getElementById(parentId);
        const selector = newElem.classList.contains('mobile-facets__details')
          ? '.mobile-facets__close-button'
          : '.facets__summary';
        const toFocus = newElem.querySelector(selector);
        const isText = event.target.getAttribute('type') === 'text';
        if (toFocus && !isText) toFocus.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((sel) => {
      const act = html.querySelector(sel);
      if (act) document.querySelector(sel).innerHTML = act.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach((sel) => {
      const source = html.querySelector(sel);
      const target = document.querySelector(sel);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const drawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    // bindEvents solo existe si el custom element ya se ha registrado (global.js)
    if (drawer && typeof drawer.bindEvents === 'function') drawer.bindEvents();
  }

  static renderCounts(source, target) {
    const tgtSum = target.querySelector('.facets__summary');
    const srcSum = source.querySelector('.facets__summary');
    if (tgtSum && srcSum) tgtSum.outerHTML = srcSum.outerHTML;

    const tgtHdr = target.querySelector('.facets__header');
    const srcHdr = source.querySelector('.facets__header');
    if (tgtHdr && srcHdr) tgtHdr.outerHTML = srcHdr.outerHTML;

    const tgtWrap = target.querySelector('.facets-wrap');
    const srcWrap = source.querySelector('.facets-wrap');
    if (tgtWrap && srcWrap) {
      const showingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (showingMore) {
        srcWrap.querySelectorAll('.facets__item.hidden').forEach((hi) =>
          hi.classList.replace('hidden', 'show-more-item')
        );
      }
      tgtWrap.outerHTML = srcWrap.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const tgtList = target.querySelector('.mobile-facets__list');
    const srcList = source.querySelector('.mobile-facets__list');
    if (tgtList && srcList) tgtList.outerHTML = srcList.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`
    );
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    if (!grid) return [];
    return [{ section: grid.dataset.id }];
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const forms = Array.from(document.querySelectorAll('facet-filters-form form'));
    if (event.srcElement.className === 'mobile-facets__checkbox') {
      const sp = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(sp, event);
    } else {
      const parts = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      forms.forEach((f) => {
        if (!isMobile) {
          if (['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(f.id)) {
            parts.push(this.createSearchParams(f));
          }
        } else if (f.id === 'FacetFiltersFormMobile') {
          parts.push(this.createSearchParams(f));
        }
      });
      this.onSubmitForm(parts.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const q = event.currentTarget.href.includes('?')
      ? event.currentTarget.href.split('?')[1]
      : '';
    FacetFiltersForm.renderPage(q);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


// ------------------------------
// price-range.js
// ------------------------------

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((el) => {
      el.addEventListener('change', this.onRangeChange.bind(this));
      el.addEventListener('keydown', this.onKeyDown.bind(this));
      el.addEventListener('input', this.onRangeChange.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const allowed = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(allowed)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (!minInput.value) maxInput.setAttribute('data-min', '0');
    if (!maxInput.value) minInput.setAttribute('data-max', minInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const v = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (v < min) input.value = min;
    if (v > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);


// ------------------------------
// price-slider-init.js
// ------------------------------

let originalMinPrice = null;
let originalMaxPrice = null;

function parseFormattedInteger(str) {
  const s = str.trim();
  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  let cleaned;

  if (hasDot && hasComma) {
    if (s.lastIndexOf('.') > s.lastIndexOf(',')) {
      cleaned = s.replace(/,/g, '');
    } else {
      cleaned = s.replace(/\./g, '').replace(/,/g, '.');
    }
  } else if (hasComma && !hasDot) {
    cleaned = s.replace(/,/g, '.');
  } else {
    cleaned = s.replace(/[^0-9.]/g, '');
  }

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : Math.trunc(num);
}

function initPriceSlider() {
  const slider = document.getElementById('price-slider');
  const inputMin = document.getElementById('Filter-Price-GTE');
  const inputMax = document.getElementById('Filter-Price-LTE');
  if (!slider || !inputMin || !inputMax) return;

  // Al primer init, capturamos los límites originales
  if (originalMinPrice === null && originalMaxPrice === null) {
    originalMinPrice = parseFormattedInteger(inputMin.getAttribute('data-min'));
    originalMaxPrice = parseFormattedInteger(inputMax.getAttribute('data-max'));
  }

  // Destruir instancia previa
  if (slider.noUiSlider) slider.noUiSlider.destroy();

  // Leemos los valores seleccionados actualmente
  const selectedMin = parseFormattedInteger(inputMin.value || inputMin.getAttribute('data-min'));
  const selectedMax = parseFormattedInteger(inputMax.value || inputMax.getAttribute('data-max'));

  // Creamos el slider con dominio ORIGINAL y manejadores en la selección actual
  noUiSlider.create(slider, {
    start: [selectedMin, selectedMax],
    connect: true,
    range: {
      min: originalMinPrice,
      max: originalMaxPrice
    }
  });

  // 1) Actualiza inputs mientras arrastras
  slider.noUiSlider.on('update', (values, handle) => {
    if (handle === 0) inputMin.value = Math.round(values[0]);
    else inputMax.value = Math.round(values[1]);
  });

  // 2) Al soltar, solo disparar el filtrado (sin cambiar dominio)
  slider.noUiSlider.on('change', () => {
    // Disparar evento para que FacetFiltersForm recoja el cambio
    const facetForm = document.querySelector('facet-filters-form form');
    if (facetForm) {
      facetForm.dispatchEvent(new Event('input', { bubbles: true }));
    }
  });

  // 3) Si editas manualmente los inputs, actualiza el slider.
  //    initPriceSlider se re-ejecuta tras cada filtrado; si estos inputs NO se
  //    reemplazaron en el render (el filtro activo se excluye a propósito), el
  //    listener se apilaría en cada pasada y el slider daría saltos. El guard
  //    dataset asegura que se enlaza una sola vez por input.
  [inputMin, inputMax].forEach((input) => {
    if (input.dataset.sliderBound === 'true') return;
    input.dataset.sliderBound = 'true';
    input.addEventListener('change', () => {
      const vMin = parseFormattedInteger(inputMin.value);
      const vMax = parseFormattedInteger(inputMax.value);
      slider.noUiSlider.set([vMin, vMax]);
    });
  });
}

// Inicializar en carga de página
document.addEventListener('DOMContentLoaded', initPriceSlider);

